import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { 
  Gift, Users, UserPlus, Plus, Edit2, Trash2, Archive, X, 
  Loader2, Save, DollarSign, MapPin, Calendar, TrendingUp
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminRewards = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('visit');
  
  // Rewards state
  const [rewards, setRewards] = useState({ visit: [], signup: [], referral: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Locations for dropdown
  const [locations, setLocations] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '', rewardType: 'visit', rewardValue: '', rewardUnit: 'credit',
    disclaimerCondition: '', minimumSpend: '', locationId: '', bonusEnabled: false,
    bonusReward: '', expirationDays: '', active: true
  });

  useEffect(() => {
    fetchLocations();
    fetchAllRewards();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/locations?practiceId=demo-practice-001`);
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchAllRewards = async () => {
    setLoading(true);
    try {
      const [visitRes, signupRes, referralRes] = await Promise.all([
        fetch(`${API}/rewards?practiceId=demo-practice-001&rewardType=visit`),
        fetch(`${API}/rewards?practiceId=demo-practice-001&rewardType=signup`),
        fetch(`${API}/rewards?practiceId=demo-practice-001&rewardType=referral`)
      ]);
      
      const [visitData, signupData, referralData] = await Promise.all([
        visitRes.json(), signupRes.json(), referralRes.json()
      ]);
      
      setRewards({ visit: visitData, signup: signupData, referral: referralData });
    } catch (err) {
      console.error('Error fetching rewards:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        rewardValue: parseFloat(formData.rewardValue) || 0,
        minimumSpend: parseFloat(formData.minimumSpend) || 0,
        bonusReward: parseFloat(formData.bonusReward) || 0,
        expirationDays: parseInt(formData.expirationDays) || 0,
        practiceId: 'demo-practice-001'
      };

      const url = editingReward ? `${API}/rewards/${editingReward.id}` : `${API}/rewards`;
      const method = editingReward ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save reward');
      toast({ title: editingReward ? 'Reward Updated' : 'Reward Created', description: `${formData.name} has been saved.` });
      resetForm();
      fetchAllRewards();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save reward.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (reward) => {
    setEditingReward(reward);
    setFormData({
      name: reward.name || '', rewardType: reward.rewardType || 'visit',
      rewardValue: reward.rewardValue?.toString() || '', rewardUnit: reward.rewardUnit || 'credit',
      disclaimerCondition: reward.disclaimerCondition || '', minimumSpend: reward.minimumSpend?.toString() || '',
      locationId: reward.locationId || '', bonusEnabled: reward.bonusEnabled || false,
      bonusReward: reward.bonusReward?.toString() || '', expirationDays: reward.expirationDays?.toString() || '',
      active: reward.active !== false
    });
    setShowForm(true);
  };

  const handleArchive = async (reward) => {
    if (!window.confirm(`Archive "${reward.name}"?`)) return;
    try {
      await fetch(`${API}/rewards/${reward.id}/archive`, { method: 'PUT' });
      toast({ title: 'Archived', description: `${reward.name} has been archived.` });
      fetchAllRewards();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to archive reward.', variant: 'destructive' });
    }
  };

  const handleDelete = async (reward) => {
    if (!window.confirm(`Delete "${reward.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/rewards/${reward.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${reward.name} has been deleted.` });
      fetchAllRewards();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete reward.', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', rewardType: activeTab, rewardValue: '', rewardUnit: 'credit',
      disclaimerCondition: '', minimumSpend: '', locationId: '', bonusEnabled: false,
      bonusReward: '', expirationDays: '', active: true
    });
    setEditingReward(null);
    setShowForm(false);
  };

  const openNewRewardForm = () => {
    resetForm();
    setFormData(prev => ({ ...prev, rewardType: activeTab }));
    setShowForm(true);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : 'Unknown';
  };

  const renderRewardCard = (reward) => (
    <Card key={reward.id} className={`${!reward.active ? 'opacity-60' : ''}`}>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              {reward.name}
              {!reward.active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
            </CardTitle>
            <CardDescription className="flex items-center gap-1 mt-1">
              <MapPin className="h-3 w-3" />
              {getLocationName(reward.locationId)}
            </CardDescription>
          </div>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" onClick={() => handleEdit(reward)}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleArchive(reward)}>
              <Archive className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => handleDelete(reward)} className="text-red-500 hover:text-red-700">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">Reward Value</span>
            <span className="font-semibold text-green-600">
              {reward.rewardUnit === 'percentage' ? `${reward.rewardValue}%` : `$${reward.rewardValue}`}
              {reward.rewardUnit === 'credit' && ' credit'}
            </span>
          </div>
          {reward.minimumSpend > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Min. Spend</span>
              <span className="text-sm">${reward.minimumSpend}</span>
            </div>
          )}
          {reward.expirationDays > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Expires</span>
              <span className="text-sm">{reward.expirationDays} days</span>
            </div>
          )}
          {reward.disclaimerCondition && (
            <p className="text-xs text-gray-500 mt-2 pt-2 border-t">{reward.disclaimerCondition}</p>
          )}
          {reward.bonusEnabled && (
            <div className="mt-2 p-2 bg-yellow-50 rounded text-sm">
              <span className="font-medium text-yellow-800">Bonus: </span>
              <span className="text-yellow-700">${reward.bonusReward} extra</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  const renderTabContent = (type, icon, title, description) => {
    const rewardsList = rewards[type] || [];
    
    return (
      <TabsContent value={type} className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {icon}
              {title}
            </h2>
            <p className="text-gray-500 text-sm">{description}</p>
          </div>
          <Button onClick={openNewRewardForm} data-testid={`add-${type}-reward-btn`}>
            <Plus className="h-4 w-4 mr-2" />
            Create Reward
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : rewardsList.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="text-center py-12">
              <Gift className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {title.toLowerCase()}</h3>
              <p className="text-gray-500 mb-4">Create your first {type} reward</p>
              <Button onClick={openNewRewardForm}>
                <Plus className="h-4 w-4 mr-2" />Create Reward
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rewardsList.map(renderRewardCard)}
          </div>
        )}
      </TabsContent>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-rewards">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Rewards</h1>
        <p className="text-gray-600">Create and manage reward programs for your patients</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Visit Rewards</p>
                <p className="text-2xl font-bold">{rewards.visit.length}</p>
              </div>
              <Calendar className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Signup Rewards</p>
                <p className="text-2xl font-bold">{rewards.signup.length}</p>
              </div>
              <UserPlus className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Referral Rewards</p>
                <p className="text-2xl font-bold">{rewards.referral.length}</p>
              </div>
              <Users className="h-8 w-8 text-purple-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Active</p>
                <p className="text-2xl font-bold">
                  {[...rewards.visit, ...rewards.signup, ...rewards.referral].filter(r => r.active).length}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-primary opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3 max-w-md">
          <TabsTrigger value="visit" className="gap-2">
            <Calendar className="h-4 w-4" />
            Visit
          </TabsTrigger>
          <TabsTrigger value="signup" className="gap-2">
            <UserPlus className="h-4 w-4" />
            Signup
          </TabsTrigger>
          <TabsTrigger value="referral" className="gap-2">
            <Users className="h-4 w-4" />
            Referral
          </TabsTrigger>
        </TabsList>

        {renderTabContent('visit', <Calendar className="h-5 w-5 text-blue-500" />, 'Visit Rewards', 'Reward patients for each visit to your spa')}
        {renderTabContent('signup', <UserPlus className="h-5 w-5 text-green-500" />, 'Signup Rewards', 'Incentivize new membership sign-ups')}
        {renderTabContent('referral', <Users className="h-5 w-5 text-purple-500" />, 'Referral Rewards', 'Reward patients for referring friends and family')}
      </Tabs>

      {/* Reward Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingReward ? 'Edit Reward' : 'Create New Reward'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="rName">Reward Name *</Label>
                  <Input id="rName" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., First Visit Bonus" required className="mt-1" />
                </div>

                <div>
                  <Label htmlFor="rType">Reward Type</Label>
                  <select id="rType" value={formData.rewardType} onChange={(e) => setFormData({...formData, rewardType: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="visit">Visit Reward</option>
                    <option value="signup">Signup Reward</option>
                    <option value="referral">Referral Reward</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rValue">Reward Value *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="rValue" type="number" value={formData.rewardValue} onChange={(e) => setFormData({...formData, rewardValue: e.target.value})} placeholder="25" required className="pl-10" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="rUnit">Reward Unit</Label>
                    <select id="rUnit" value={formData.rewardUnit} onChange={(e) => setFormData({...formData, rewardUnit: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="credit">Account Credit</option>
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed Discount</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="rMinSpend">Minimum Spend ($)</Label>
                    <Input id="rMinSpend" type="number" value={formData.minimumSpend} onChange={(e) => setFormData({...formData, minimumSpend: e.target.value})} placeholder="0" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="rExpiry">Expiration (days)</Label>
                    <Input id="rExpiry" type="number" value={formData.expirationDays} onChange={(e) => setFormData({...formData, expirationDays: e.target.value})} placeholder="0 = never" className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="rLocation">Location</Label>
                  <select id="rLocation" value={formData.locationId} onChange={(e) => setFormData({...formData, locationId: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="rCondition">Disclaimer/Condition</Label>
                  <Textarea id="rCondition" value={formData.disclaimerCondition} onChange={(e) => setFormData({...formData, disclaimerCondition: e.target.value})} placeholder="e.g., Valid for first-time visitors only" rows={2} className="mt-1" />
                </div>

                <div className="p-3 bg-yellow-50 rounded-lg space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.bonusEnabled} onChange={(e) => setFormData({...formData, bonusEnabled: e.target.checked})} className="rounded" />
                    <span className="text-sm font-medium">Enable Bonus Reward</span>
                  </label>
                  {formData.bonusEnabled && (
                    <div>
                      <Label htmlFor="rBonus">Bonus Amount ($)</Label>
                      <Input id="rBonus" type="number" value={formData.bonusReward} onChange={(e) => setFormData({...formData, bonusReward: e.target.value})} placeholder="10" className="mt-1" />
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({...formData, active: e.target.checked})} className="rounded" />
                  <span className="text-sm">Active</span>
                </label>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingReward ? 'Update' : 'Create'} Reward</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminRewards;
